import * as i18next from 'i18next'
import { t } from 'i18next'
import * as moment from 'moment'
import { DefaultButton } from 'office-ui-fabric-react/lib/Button'
import { Checkbox } from 'office-ui-fabric-react/lib/Checkbox'
import { ChoiceGroup } from 'office-ui-fabric-react/lib/ChoiceGroup'
import { DatePicker } from 'office-ui-fabric-react/lib/DatePicker'
import { Label } from 'office-ui-fabric-react/lib/Label'
import { IStackStyles, Stack } from 'office-ui-fabric-react/lib/Stack'
import { Text } from 'office-ui-fabric-react/lib/Text'
import { TextField } from 'office-ui-fabric-react/lib/TextField'
import * as React from 'react'
import Select from 'react-select'
import { AsyncState, Updater } from 'widgets-for-react'
import { getVacancyIdFromUrl, parseParagraphs } from '../publicSite/utils/helpers'
import { postFormSubmissionState } from './api'
import { status_error_renderer, status_sending_renderer, status_thank_you_renderer } from './status_message_renderers'
import { DependenciesState, DependencyData, FieldData, FormData, FormState, StepData } from './types'
import {
  are_all_dependencies_met,
  are_dependencies_met,
  DayPickerStrings,
  elementInArrayUpdater,
  is_dependency_met,
  update_field_AUX,
  update_field_file
} from './utils'

export function parseRawHtml(input: string): HTMLElement {
  let elem = document.createElement('span')

  elem.innerHTML = input

  return elem
}

const debug = false

export let show_optional = (input: boolean) => {
  return input ? '' : ' form__label--optional'
}

// --- de_Copacking English-mode overrides (SREF-281) ---
// When the user selects "Anderes Land" in the Land dropdown on the de_Copacking
// form, labels and options switch to English via i18next bundle de_copacking_en.
// The Land dropdown's own options must stay German so the user can switch back.
const COPACKING_FORM_TITLE = 'de_Copacking'
const COPACKING_LAND_FIELD_TITLE = 'Land'
const COPACKING_EN_TRIGGER_VALUE = 'Anderes Land'
const COPACKING_EN_NAMESPACE = 'de_copacking_en'

type CopackingEnBundle = {
  labels?: Record<string, string>
  options?: Record<string, string>
  ui?: Record<string, string>
}

export const isCopackingEnglishMode = (formState: FormState): boolean => {
  if (formState.form_data.kind !== 'loaded') return false
  const data = formState.form_data.value
  if (data.form.title !== COPACKING_FORM_TITLE) return false
  return data.steps.some((s) =>
    s.fields.some((f) => f.title === COPACKING_LAND_FIELD_TITLE && f.user_input === COPACKING_EN_TRIGGER_VALUE)
  )
}

type I18NextInstance = {
  language: string
  getResourceBundle: (lng: string, ns: string) => unknown
}

const getI18NextInstance = (): I18NextInstance => {
  const mod = i18next as unknown as { default?: I18NextInstance } & I18NextInstance
  return mod.default ?? mod
}

const getCopackingEnBundle = (): CopackingEnBundle => {
  const inst = getI18NextInstance()
  const bundle = inst.getResourceBundle(inst.language, COPACKING_EN_NAMESPACE)
  return (bundle as CopackingEnBundle) ?? {}
}

export const translateCopackingLabel = (title: string, isEn: boolean): string => {
  if (!isEn) return title
  return getCopackingEnBundle().labels?.[title] ?? title
}

export const translateCopackingOption = (value: string, isEn: boolean): string => {
  if (!isEn) return value
  return getCopackingEnBundle().options?.[value] ?? value
}

export const translateCopackingUi = (text: string, isEn: boolean): string => {
  if (!isEn) return text
  return getCopackingEnBundle().ui?.[text] ?? text
}

export type fieldWidthsInput = 'Large' | 'Medium' | 'Small' | 'Extra small'
export type fieldWidthsOutput = '' | ' form__group--m' | ' form__group--s' | ' form__group--xs'

export const fieldsWidth = (input: fieldWidthsInput): fieldWidthsOutput => {
  let output: fieldWidthsOutput = ''

  if (input == 'Extra small') {
    output = ' form__group--xs'
  } else if (input == 'Small') {
    output = ' form__group--s'
  } else if (input == 'Medium') {
    output = ' form__group--m'
  }

  return output
}

export let dependency_renderer = (dependency: DependencyData, formState: FormState) => (
  <Stack horizontal horizontalAlign={'start'} gap={5}>
    <Text variant={'medium'}>
      <b>Title</b>: {dependency.dependency.title}
    </Text>
    <Text variant={'medium'}>
      <b>Is met?</b> {`${is_dependency_met(dependency.dependency, formState)}`}
    </Text>
    <Text variant={'medium'}>
      <b>Hide if violated?</b> {`${dependency.dependency.hideIfViolated}`}
    </Text>
    <Text variant={'medium'}>
      <b>Kind</b>: {dependency.dependency.Kind}
    </Text>
    <Text variant={'medium'}>
      <b>Dependent fields</b>: {dependency.dependent_fields.map((df) => `${df.FieldId}`).join(',')}
    </Text>
  </Stack>
)

export const stackStyles: IStackStyles = { root: { width: `100%` } }
export const stackStyles1: IStackStyles = { root: { width: `100%` } }

export let field_renderer = (
  field: FieldData,
  formState: FormState,
  update_field: (fieldUpdater: Updater<FieldData>, updatedDependencyState: DependenciesState) => void
) => {
  let isRequired = false
  if (formState.form_data.kind == 'loaded') {
    formState.form_data.value.steps.forEach((s) =>
      s.dependencies.forEach((d) =>
        d.dependent_fields.forEach((f) => {
          if (f.FieldId == field.Id) {
            isRequired = true
          }
        })
      )
    )
  }
  const are_dependencies_met_res = are_dependencies_met(field, formState)
  const default_selected_answer = field.maybe_answers.find((a) => a.isDefaultSelected)
  const isVacancy = getVacancyIdFromUrl().kind == 'r'
  const isEn = isCopackingEnglishMode(formState)
  const isLandField = field.title === COPACKING_LAND_FIELD_TITLE
  const displayTitle = translateCopackingLabel(field.title, isEn)
  const translateAnswer = (value: string) =>
    isLandField ? value : translateCopackingOption(value, isEn)
  const errorMessages = are_dependencies_met_res.kind == 'no'
    ? are_dependencies_met_res.messages.map((m) => translateCopackingUi(m, isEn)).join(' | ')
    : ''
  switch (field.Kind) {
    case 'RadioOption':
      return (
        <div className={`radio-btn form__group${fieldsWidth(field.width_percentage)}`}>
          <Label id={`field-${field.Id}`} className={`form__label${show_optional(isRequired)}`}>
            {displayTitle}
            {isRequired ? <span className="form__label-required">*</span> : ''}
          </Label>
          <div className="radio-btn__wrap">
            <ChoiceGroup
              defaultSelectedKey={default_selected_answer != undefined ? `answer-${default_selected_answer.Id}` : ''}
              options={field.maybe_answers.map((a) => ({
                key: `answer-${a.Id}`,
                text: a.value
              }))}
              onChange={(_, selected_answer) => {
                if (field.Kind != 'RadioOption') return
                let res = update_field_AUX(field, selected_answer.text, formState)
                update_field(res.fieldDataUpdater, res.dependencyState)
              }}
            />
          </div>
        </div>
      )

    case 'Dropdown':
      return (
        <div className={`form__group${fieldsWidth(field.width_percentage)}`}>
          <Label id={`field-${field.Id}`} className={`form__label${show_optional(isRequired)}`}>
            {displayTitle}
            {isRequired ? <span className="form__label-required">*</span> : ''}
          </Label>
          <Select
            className="dropdown"
            // react-select only emits stable class names when a prefix is set;
            // without it every element carries an emotion hash. The SCSS in
            // overwrites/_fabric-ui-form.scss targets these `dropdown__*` names.
            classNamePrefix="dropdown"
            options={
              !field.useExternalSource
                ? field.maybe_answers.map((a) => ({
                    value: `answer-${a.Id}`,
                    label: translateAnswer(a.value),
                    originalValue: a.value,
                    class: `dropdown__select`
                  }))
                : !formState.context.external_options.has(field.sourceName)
                ? []
                : formState.context.external_options.get(field.sourceName).map((a) => ({
                    value: `answer-${a}`,
                    label: a,
                    originalValue: a,
                    class: `dropdown__select`
                  }))
            }
            onChange={(newValue, actionMeta) => {
              if (field.Kind != 'Dropdown') return
              if (newValue == null) return
              const submittedValue = (newValue as { originalValue?: string }).originalValue ?? newValue.label
              let res = update_field_AUX(field, submittedValue, formState)
              update_field(res.fieldDataUpdater, res.dependencyState)
            }}
            placeholder={i18next.t('form:make_a_choice')}
          />
        </div>
      )

    case 'Checkbox':
      return (
        <Stack
          className={`checkbox form__group${fieldsWidth(field.width_percentage)}${
            field.user_input != undefined && are_dependencies_met_res.kind == 'no' ? ' form__group--error' : ''
          }`}
        >
          <div className="checkbox__wrap">
            <Checkbox
              id={'checkbox-' + field.Id}
              name={'checkbox-' + field.Id}
              defaultChecked={field.optionalDefaultValue.toLowerCase() == 'true'}
              onChange={(_, checked) => {
                let res = update_field_AUX(field, `${checked ? true : ''}`.toLowerCase(), formState)
                update_field(res.fieldDataUpdater, res.dependencyState)
              }}
            ></Checkbox>
            <svg
              className="checkbox__checkmark"
              height="14"
              viewBox="0 0 16 14"
              width="16"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="m380 1834.96686 4.888931 6.03314 9.111069-12"
                fill="none"
                stroke="#FFF"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="3"
                transform="translate(-379 -1828)"
              />
            </svg>

            <label className="pseudo-label" htmlFor={'checkbox-' + field.Id}>
              <span
                className="ms-Checkbox-text"
                dangerouslySetInnerHTML={{
                  __html: displayTitle + (isRequired ? '<span class="form__label-required">*</span>' : '')
                }}
              ></span>
            </label>

            {field.user_input != undefined && are_dependencies_met_res.kind == 'no' ? (
              <div className="alert">
                <p className="ms-TextField-errorMessage error-message">{errorMessages}</p>
              </div>
            ) : null}
          </div>
        </Stack>
      )

    case 'CheckboxMultiSelect':
      return (
        <div className={`checkboxes form__group${fieldsWidth(field.width_percentage)}`}>
          <Label id={`field-${field.Id}`} className={`form__label${show_optional(isRequired)}`}>
            {displayTitle}
            {isRequired ? <span className="form__label-required">*</span> : ''}
          </Label>
          <div className="checkboxes__wrap">
            {field.maybe_answers.map((a) => {
              return (
                <div className="checkboxes__item">
                  <Checkbox
                    label={translateAnswer(a.value)}
                    defaultChecked={a.isDefaultSelected}
                    onChange={(_, selected_answer) => {
                      if (field.Kind != 'CheckboxMultiSelect') return
                      let res = null
                      if (selected_answer) {
                        res = update_field_AUX(field, field.user_input + '[' + a.value + ']', formState)
                      } else {
                        res = update_field_AUX(field, field.user_input.replace('[' + a.value + ']', ''), formState)
                      }

                      update_field(res.fieldDataUpdater, res.dependencyState)
                    }}
                  />
                  <svg
                    className="checkboxes__checkmark"
                    height="14"
                    viewBox="0 0 16 14"
                    width="16"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="m380 1834.96686 4.888931 6.03314 9.111069-12"
                      fill="none"
                      stroke="#FFF"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="3"
                      transform="translate(-379 -1828)"
                    />
                  </svg>
                </div>
              )
            })}
          </div>
        </div>
      )

    case 'SimpleDate':
      return (
        <Stack
          className={`text-input form__group${fieldsWidth(field.width_percentage)}${
            field.user_input != undefined && are_dependencies_met_res.kind == 'no' ? ' form__group--error' : ''
          }`}
        >
          <Label className={`form__label${show_optional(isRequired)}`}>
            {displayTitle}
            {isRequired ? <span className="form__label-required">*</span> : ''}
          </Label>
          <DatePicker
            firstDayOfWeek={1}
            strings={DayPickerStrings}
            placeholder={field.placeholder != undefined ? field.placeholder : ''}
            ariaLabel="Select a date"
            value={field.user_input === '' ? undefined : moment(field.user_input, 'DD-MM-YYYY').toDate()}
            minDate={
              (field.StartDate as any) == ''
                ? null
                : typeof field.StartDate == 'string'
                ? moment(field.StartDate).toDate()
                : field.StartDate.toDate()
            }
            isMonthPickerVisible={false}
            onSelectDate={(new_date) => {
              let res = update_field_AUX(field, moment(new_date, 'DD-MM-YYYY').format(), formState)
              update_field(res.fieldDataUpdater, res.dependencyState)
            }}
          />
          {field.user_input != undefined && are_dependencies_met_res.kind == 'no' ? (
            <div className="alert">
              <p className="ms-TextField-errorMessage error-message">{errorMessages}</p>
            </div>
          ) : null}
        </Stack>
      )
    case 'Attachement':
      let allowed_extensions = isVacancy ? '.doc,.docx,.pdf' : '.pdf,.jpg,.jpeg,.png'
      return (
        <Stack className={`attachment-field af form__group${fieldsWidth(field.width_percentage)}`}>
          <Label className={`form__label${show_optional(isRequired)}`}>
            {displayTitle}
            {isRequired ? <span className="form__label-required">*</span> : ''}
          </Label>
          {
            <input
              accept={allowed_extensions}
              placeholder={field.placeholder != undefined ? field.placeholder : ''}
              className={`af__input${
                field.user_input != undefined && are_dependencies_met_res.kind == 'no'
                  ? ' form__input form__input--error'
                  : ''
              }`}
              type="file"
              {...(isVacancy ? {} : { multiple: true })}
              onChange={(e: any) => {
                let files: FileList = (e.target as any).files
                if (isVacancy && files[0] != undefined) {
                  let file = files[0]
                  let extension =
                    '.' + (file.name.substring(file.name.lastIndexOf('.') + 1, file.name.length) || file.name)
                  if (!allowed_extensions.includes(extension)) {
                    e.target.value = ''
                    let res = update_field_file(field, null, 'FILE_TYPE_ERROR', formState)
                    update_field(res.fieldDataUpdater, res.dependencyState)
                    return
                  }
                }
                if (isVacancy && files[0] == undefined) {
                  let res = update_field_file(field, null, '', formState)
                  update_field(res.fieldDataUpdater, res.dependencyState)
                }
                if (files[0] && files[0].size > 30 * 1024 * 1024) {
                  e.target.value = ''
                  let res = update_field_file(field, null, 'FILE_SIZE_ERROR', formState)
                  update_field(res.fieldDataUpdater, res.dependencyState)
                  return
                } else {
                  const file = files[0]
                  if (files[0] != undefined) {
                    const extension =
                      '.' + (file.name.substring(file.name.lastIndexOf('.') + 1, file.name.length) || file.name)
                    if (!allowed_extensions.includes(extension)) {
                      window.alert(i18next.t('form:accepted_file_types_text'))
                      e.preventDefault()
                      const res = update_field_file(field, null, '', formState)
                      update_field(res.fieldDataUpdater, res.dependencyState)
                    }
                    const res = update_field_file(field, files, file.name, formState)
                    update_field(res.fieldDataUpdater, res.dependencyState)
                  } else {
                    const res = update_field_file(field, files, file.name, formState)
                    update_field(res.fieldDataUpdater, res.dependencyState)
                  }
                }
              }}
            />
          }
          {isVacancy ? (
            <p className="help-text">Max file size is 30MB. Allowed file types: .doc, .docx, .pdf</p>
          ) : (
            <p className="help-text">
              Max file size is 30MB. To select multiple items, hold down the 'Command' key (for Mac) or 'Ctrl' key (for
              Windows) while clicking on the desired items.
            </p>
          )}
          {field.user_input != undefined &&
          (are_dependencies_met_res.kind == 'no' ||
            field.user_input == 'FILE_SIZE_ERROR' ||
            field.user_input == 'FILE_TYPE_ERROR') ? (
            <div className="alert">
              <p className="ms-TextField-errorMessage error-message">
                {are_dependencies_met_res.kind == 'no' &&
                  field.user_input != 'FILE_SIZE_ERROR' &&
                  field.user_input != 'FILE_TYPE_ERROR' &&
                  errorMessages}
                {field.user_input == 'FILE_SIZE_ERROR' && i18next.t('form:accepted_file_size_text')}
                {field.user_input == 'FILE_TYPE_ERROR' && i18next.t('form:accepted_file_types_text')}
              </p>
            </div>
          ) : null}
        </Stack>
      )
    case 'ReadonlyText':
      return (
        <Stack className={`form__group form__group--paragraph${fieldsWidth(field.width_percentage)} text--styled`}>
          <div dangerouslySetInnerHTML={{ __html: parseParagraphs(field.Description) }} />
        </Stack>
      )
    default:
      return (
        <Stack
          className={`text-input form__group${fieldsWidth(field.width_percentage)}${
            field.user_input != undefined && are_dependencies_met_res.kind == 'no' ? ' form__group--error' : ''
          }`}
        >
          <Label className={`form__label${show_optional(isRequired)}`}>
            {displayTitle}
            {isRequired ? <span className="form__label-required">*</span> : ''}
          </Label>
          <TextField
            placeholder={field.placeholder != undefined ? field.placeholder : ''}
            borderless
            multiline={field.Kind == 'TextArea'}
            rows={field.Kind == 'TextArea' ? 2 : null}
            className={`form__input ${
              field.user_input != undefined && are_dependencies_met_res.kind == 'no' ? ' form__input--error' : ''
            }`}
            value={`${field.user_input ? field.user_input : ''}`}
            onChange={(e, text): void => {
              const res = update_field_AUX(field, field.Kind == 'UserEmail' ? text.replace(/ /g, '') : text, formState)
              update_field(res.fieldDataUpdater, res.dependencyState)
            }}
            onBlur={(e) => {
              if (field.Kind == 'UserEmail') {
                // Regular expression for validating email format
                const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/

                if (!emailPattern.test(e.target.value)) {
                  //   window.alert(i18next.t("form:accepted_email_text"))
                  e.preventDefault()
                  let res = update_field_AUX(field, '', formState)
                  update_field(res.fieldDataUpdater, res.dependencyState)
                  return
                }
              }
            }}
          />
          {field.user_input != undefined && are_dependencies_met_res.kind == 'no' && (
            <div className="alert">
              <p className="ms-TextField-errorMessage error-message">{errorMessages}</p>
            </div>
          )}
        </Stack>
      )
  }
}

export const step_renderer = (
  step: StepData,
  formState: FormState,
  update_step: (setUpdater: Updater<StepData>, updatedDependencyState: DependenciesState) => void
) => (
  <Stack gap={20}>
    <Stack gap={10} className="form__wrap">
      {step.fields
        .sort((a, b) => (a.itemOrderNumber < b.itemOrderNumber ? -1 : a.itemOrderNumber > b.itemOrderNumber ? 1 : 0))
        .map((field, i) =>
          field_renderer(field, formState, (fieldUpdater, updated_dependencies) =>
            update_step(
              (step): StepData => ({ ...step, fields: elementInArrayUpdater(i, fieldUpdater)(step.fields) }),
              updated_dependencies
            )
          )
        )}
    </Stack>
    {debug && (
      <Stack>
        <Text variant={'large'}> Dependencies: </Text>
        <Stack gap={10}>{step.dependencies.map((dependency) => dependency_renderer(dependency, formState))}</Stack>
      </Stack>
    )}
  </Stack>
)

// Set href so a hard load is done so Meta Pixel works properly: https://hoppinger.atlassian.net/browse/SREF-196
const redirectToSuccessPage = () =>
  (window.location.href = window.location.href + (window.location.href.endsWith('/') ? '' : '/') + 'success/')

export const form_renderer = (
  data: FormData,
  formState: FormState,
  update_form_submission_logic: (updated_form_submission_logic: AsyncState<string>) => void,
  update_form: (formDataUpdater: Updater<FormData>, updatedDependencyState: DependenciesState) => void
) => {
  if (
    formState.form_submission_logic.kind == 'loaded' &&
    formState.form_submission_logic.value == 'ok' &&
    formState.form_data.kind == 'loaded' &&
    data.form.title?.startsWith('vacancyapplication2')
  ) {
    redirectToSuccessPage()
    return <></>
  }

  const isEn = isCopackingEnglishMode(formState)

  return (
    <Stack styles={stackStyles} className="form">
      {formState.form_submission_logic.kind == 'loading' && status_sending_renderer()}
      {formState.form_submission_logic.kind == 'loaded' && formState.form_submission_logic.value != 'ok' && (
        <p className="message message--error">{t(`form:${formState.form_submission_logic.value}`)}</p>
      )}
      {formState.form_submission_logic.kind == 'loaded' &&
        formState.form_submission_logic.value == 'ok' &&
        formState.form_data.kind == 'loaded' &&
        status_thank_you_renderer(translateCopackingUi(formState.form_data.value.thank_you.content, isEn))}
      {formState.form_submission_logic.kind == 'connection-error' &&
        formState.form_submission_logic.retryCount >= 10 &&
        status_error_renderer()}
      {formState.form_submission_logic.kind == 'loaded' &&
      formState.form_submission_logic.value == 'ok' &&
      formState.form_data.kind == 'loaded' ? null : (
        <Stack className="form__inner">
          <Stack className="form__content" gap={20}>
            {data.steps
              .sort((a, b) =>
                a.step.orderNumber < b.step.orderNumber ? -1 : a.step.orderNumber > b.step.orderNumber ? 1 : 0
              )
              .map((step, i) =>
                step_renderer(step, formState, (stepUpdater, updated_dependencies) =>
                  update_form(
                    (formData): FormData => ({
                      ...formData,
                      steps: elementInArrayUpdater(i, stepUpdater)(formData.steps)
                    }),
                    updated_dependencies
                  )
                )
              )}
          </Stack>
          <DefaultButton
            onClick={() => {
              update_form_submission_logic(postFormSubmissionState(data.form.Id, formState))
            }}
            disabled={!are_all_dependencies_met(formState)}
            text={translateCopackingUi(
              i18next.t(
                data.form.title?.startsWith('vacancyapplication2') ? 'applicationform:form_submit' : 'form:form_submit'
              ),
              isEn
            )}
            type="button"
            className="button button--primary form__button"
          />
        </Stack>
      )}
    </Stack>
  )
}
